import { Lead } from "@/app/entities/Lead";
import { useDebounce } from "@/app/hooks/useDebounce";
import { useGetAllUserLinks } from "@/app/hooks/useGetAllUserLinks";
import { useGetLeads } from "@/app/hooks/useGetLeads";
import { useLeadMutations } from "@/app/hooks/useLeadMutations";
import { configService } from "@/app/services/configService";
import { ProductCatalogItem } from "@/app/services/configService/getProductsCatalog";
import { HandleLeadStatusParams } from "@/app/services/leadsService/handleLeadStatus";
import { selfHireService } from "@/app/services/selfHireService";
import { Links } from "@/app/services/selfHireService/getAllUserLinks";
import { PartnerType } from "@/app/utils/partners.enum";
import { EmptyContent } from "@/view/components/EmptyContent";
import { useToast } from "@/view/components/ui/use-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AArrowDown, Calendar, Search, Users, Wallet } from "lucide-react";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";

export const useLinksController = () => {
  const [searchValue, setSearchValue] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState({ name: "" });
  const [isCreateLinkModalOpen, setIsCreateLinkModalOpen] = useState(false);
  const [newLinkName, setNewLinkName] = useState("");
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const debouncedSearchValue = useDebounce(500, searchValue);
  const navigate = useNavigate();

  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [isModalDetailsOpen, setIsModalDetailsOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<"leads" | "links">("leads");
  const { updateLeadStatus } = useLeadMutations();
  const [searchValueLead, setSearchValueLead] = useState("");
  const debouncedSearchValueLead = useDebounce(500, searchValueLead);
  const [contactedLeads, setContactedLeads] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<string>("createdAt");
  const [order, setOrder] = useState<1 | -1>(-1);
  const [page, setPage] = useState(1);
  const limit = 10;

  const openDetailsModal = (lead: Lead) => {
    setSelectedLeadId(lead._id);
    setIsModalDetailsOpen(true);
  };

  const closeModal = () => {
    setIsModalDetailsOpen(false);
    setSelectedLeadId(null);
  };

  const { data: productsCatalog } = useQuery<ProductCatalogItem[]>({
    queryKey: ["productsCatalog"],
    queryFn: configService.getProductsCatalog,
  });

  const {
    data: leadsData,
    isFetching: isFetchingLeads,
    setParams: setParamsLeads,
    refetch: refetchLeads,
  } = useGetLeads({ limit });

  const setSearchLead = useCallback(() => {
    setParamsLeads((state) => ({
      ...state,
      search: debouncedSearchValueLead,
      skip: 0,
    }));
    setPage(1);
  }, [debouncedSearchValueLead, setParamsLeads]);

  useEffect(() => {
    setSearchLead();
  }, [setSearchLead]);

  const handleSearchInputChangeLead = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchValueLead(event.target.value);
  };

  const openWhatsApp = ({ _id: id, phone }: { _id: string; phone: string }) => {
    const cleanedPhone = `55${phone.replace(/\D/g, "").slice(-11)}`;
    const text = "Olá, gostaria de simular uma nova antecipação do FGTS?";
    const encodedText = encodeURIComponent(text);
    const url = `https://wa.me/${cleanedPhone}?text=${encodedText}`;

    handleLeadStatus({ id });
    window.open(url, "_blank");
  };

  const handleLeadStatus = async (input: HandleLeadStatusParams) => {
    try {
      await updateLeadStatus(input);
      setContactedLeads((prev) => new Set(prev).add(input.id));
      await refetchLeads();
    } catch (error: any) {}
  };

  useEffect(() => {
    if (leadsData) {
      const contactedFromData = new Set(
        leadsData.leads.filter((c) => c.contacted).map((c) => c._id),
      );
      setContactedLeads(contactedFromData);
    }
  }, [leadsData]);

  const handleSortChange = (newSort: string) => {
    setSort((prevSort) => {
      if (prevSort === newSort) {
        setOrder((prevOrder) => (prevOrder === 1 ? -1 : 1));
        return newSort;
      } else {
        setOrder(newSort === "name" ? 1 : -1);
        return newSort;
      }
    });
    setPage(1);
  };

  const sortIcon = useMemo(() => {
    switch (sort) {
      case "createdAt":
        return <Calendar size={20} className="text-primaryHex" />;
      case "availableBalance":
        return <Wallet size={20} className="text-primaryHex" />;
      case "name":
        return <AArrowDown size={20} className="text-primaryHex" />;
      default:
        return <Search size={20} className="text-primaryHex" />;
    }
  }, [sort]);

  useEffect(() => {
    setParamsLeads((prev) => ({
      ...prev,
      sort,
      order: String(order),
      skip: (page - 1) * limit,
    }));
  }, [page, sort, order, setParamsLeads]);

  const renderNoLeads = () => {
    const noLeads = leadsData?.leads.length === 0;
    return noLeads ? (
      <EmptyContent
        icon={<Users size={46} />}
        title="Ainda não há leads"
        description="Quando um cliente começar a autocontratação e parar no meio, ele surgirá aqui para que você entre em ação e feche a venda."
      />
    ) : null;
  };

  const totalPages = Math.ceil((leadsData?.pagination.total || 0) / limit);

  const handlePreviousLeads = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextLeads = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const { ref, inView } = useInView();

  const { toast } = useToast();

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const {
    data,
    isFetching,
    setParams,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetAllUserLinks();

  const getPartnerTypeFromTableName = (name: string): PartnerType => {
    const normalized = name?.toUpperCase()?.trim();

    if (normalized in PartnerType) {
      return normalized as PartnerType;
    }

    return PartnerType.FGTS;
  };

  const getPartnerTypes = (tables: { type: string }[]): PartnerType[] => {
    const result = new Set<PartnerType>();

    for (const table of tables || []) {
      const partnerType = getPartnerTypeFromTableName(table.type);
      result.add(partnerType);
    }

    return Array.from(result);
  };

  const setSearch = useCallback(() => {
    setParams((state) => ({ ...state, search: debouncedSearchValue }));
  }, [debouncedSearchValue, setParams]);

  const { mutate: createLinkMutation, isPending } = useMutation({
    mutationFn: async ({
      name,
      label,
      tables,
    }: {
      name: string;
      label: string;
      tables: string[];
    }) => {
      return await selfHireService.createLink({ name, label, tables });
    },
    onSuccess: (variables) => {
      queryClient.invalidateQueries({ queryKey: ["AllUserLinks"] });

      setIsCreateLinkModalOpen(false);

      setNewLinkName("");
      setSelectedTables([]);

      setSelectedLink({ name: variables.name });
      setIsModalOpen(true);

      toast({
        variant: "default",
        title: "Link criado com sucesso!",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title:
          error?.message || "Não foi possível criar o link, tente novamente",
      });
    },
  });

  const handleOpenModal = (link: { name: string }) => {
    setSelectedLink({ name: link.name });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCreateLink = () => {
    if (newLinkName && selectedTables) {
      createLinkMutation({
        name: newLinkName.replace(/\s/g, ""),
        label: newLinkName.trim(),
        tables: selectedTables,
      });
    }
  };

  const handleLinkClick = (link: Links) => {
    if (data?.pages?.[0]?.config.details) return navigate(`/links/${link._id}`);
    return handleOpenModal(link);
  };

  useEffect(() => {
    setSearch();
  }, [setSearch]);

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView]);

  return {
    data,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    handleSearchInputChange,
    ref,
    searchValue,
    isModalOpen,
    selectedLink,
    isCreateLinkModalOpen,
    newLinkName,
    selectedTables,
    setIsCreateLinkModalOpen,
    setNewLinkName,
    setSelectedTables,
    handleOpenModal,
    handleCloseModal,
    handleCreateLink,
    isPending,
    handleLinkClick,
    selectedTab,
    setSelectedTab,
    leadsData,
    isFetchingLeads,
    searchValueLead,
    handleSearchInputChangeLead,
    openWhatsApp,
    contactedLeads,
    sort,
    handleSortChange,
    sortIcon,
    renderNoLeads,
    page,
    totalPages,
    handlePreviousLeads,
    handleNextLeads,
    selectedLeadId,
    isModalDetailsOpen,
    openDetailsModal,
    closeModal,
    getPartnerTypes,
    productsCatalog,
  };
};
